import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './InlineMarkupEditor.css';

/**
 * Props:
 *  - content (HTML)
 *  - onChange(html, plainText)
 *  - inlineErrors: [{start?,end?,type?,message?/explanation?,originalText?,correction?}]
 *  - showMarkups: boolean
 *  - activeRange?: {start?,end?,originalText?}
 *  - placeholder?: string
 *  - modules?: Quill modules
 *  - readOnly?: boolean   <-- NEW (use true on results page)
 */
const InlineMarkupEditor = ({
  content,
  onChange,
  inlineErrors = [],
  showMarkups = true,
  activeRange = null,
  placeholder = 'Start writing...',
  modules = null,
  readOnly = false
}) => {
  const [editorContent, setEditorContent] = useState(content || '');
  const quillRef = useRef(null);
  const isApplyingRef = useRef(false);

  // Default toolbar (hidden when readOnly)
  const defaultModules = useMemo(
    () =>
      readOnly
        ? { toolbar: false }
        : {
            toolbar: {
              container: [
                [{ header: '1' }, { header: '2' }, { font: [] }],
                [{ size: [] }],
                ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                [{ list: 'ordered' }, { list: 'bullet' }],
                ['link', 'image'],
                [{ color: [] }, { background: [] }],
                ['clean'],
              ],
            },
          },
    [readOnly]
  );

  // ✅ 'background' required for highlights
  const defaultFormats = useMemo(
    () => [
      'header', 'font', 'size',
      'bold', 'italic', 'underline', 'strike', 'blockquote',
      'list', 'bullet',
      'link', 'image',
      'color', 'background'
    ],
    []
  );

  // Helpers
  const stripHtml = (h) => {
    const d = document.createElement('div');
    d.innerHTML = h || '';
    return d.textContent || d.innerText || '';
  };
  const safeStr = (v) => (typeof v === 'string' ? v : '');
  const asNum = (v, d = null) => (Number.isFinite(+v) ? +v : d);

  // Normalize marks
  const normalizeMarks = useMemo(() => {
    const list = Array.isArray(inlineErrors) ? inlineErrors : [];
    return list.map((m) => ({
      start: asNum(m?.start, null),
      end: asNum(m?.end, null),
      type: safeStr(m?.type || 'other'),
      message: safeStr(m?.message || m?.explanation || ''),
      originalText: safeStr(m?.originalText || ''),
      correction: safeStr(m?.correction || '')
    }));
  }, [inlineErrors]);

  // Sync external content → editor
  useEffect(() => {
    if (content !== editorContent && !isApplyingRef.current) {
      setEditorContent(content || '');
    }
  }, [content, editorContent]);

  const handleContentChange = useCallback((value) => {
    if (isApplyingRef.current) return;
    setEditorContent(value);
    onChange?.(value, stripHtml(value));
  }, [onChange]);

  const getColor = (type) => {
    const colors = {
      grammar: 'rgba(255, 87, 34, 0.28)',
      spelling: 'rgba(244, 67, 54, 0.28)',
      punctuation: 'rgba(156, 39, 176, 0.28)',
      agreement: 'rgba(33, 150, 243, 0.28)',
      structure: 'rgba(76, 175, 80, 0.28)',
      argument: 'rgba(103, 58, 183, 0.28)',
      style: 'rgba(233, 30, 99, 0.28)',
      cohesion: 'rgba(54, 162, 235, 0.28)',
      content: 'rgba(75, 192, 192, 0.28)',
      register: 'rgba(201, 203, 207, 0.28)',
      other: 'rgba(0, 0, 0, 0.12)'
    };
    return colors[type] || colors.other;
  };

  const applyInlineMarkups = useCallback(() => {
    const quill = quillRef.current?.getEditor?.();
    if (!quill) return;

    const qText = quill.getText() || '';
    const lower = qText.toLowerCase();
    isApplyingRef.current = true;

    try {
      // clear all previous backgrounds
      quill.formatText(0, qText.length, 'background', false);

      if (!showMarkups || !normalizeMarks.length) return;

      // --- A) Prefer string search by originalText (covers most cases) ---
      for (const m of normalizeMarks) {
        const needle = (m.originalText || '').trim().toLowerCase();
        if (!needle) continue;

        // find ALL occurrences, not just the first
        let from = 0;
        while (from <= lower.length - needle.length) {
          const idx = lower.indexOf(needle, from);
          if (idx === -1) break;
          quill.formatText(idx, Math.max(1, needle.length), 'background', getColor(m.type));
          from = idx + Math.max(1, needle.length);
        }
      }

      // --- B) Fall back to numeric ranges if provided and NOT already covered above ---
      for (const m of normalizeMarks) {
        if (!(Number.isFinite(m.start) && Number.isFinite(m.end))) continue;
        const s = clamp(0, qText.length, m.start);
        const e = clamp(0, qText.length, m.end);
        const len = Math.max(0, e - s);
        if (len > 0) {
          quill.formatText(s, len, 'background', getColor(m.type));
        }
      }
    } catch (err) {
      console.warn('Error applying inline markups (handled):', err);
    } finally {
      isApplyingRef.current = false;
      setTimeout(() => quill.update?.(), 20);
    }
  }, [normalizeMarks, showMarkups]);


  // Ensure we apply after Quill is mounted
  useEffect(() => {
    // Wait until Quill is ready, then apply after a tick so the DOM is painted
    const quill = quillRef.current?.getEditor?.();
    if (!quill) return;
    const t = setTimeout(applyInlineMarkups, 80);
    return () => clearTimeout(t);
  }, [applyInlineMarkups, editorContent, showMarkups, normalizeMarks]);

  // Jump to range: now prefers numeric offsets (start/end), fallback to originalText
  useEffect(() => {
    if (!activeRange) return;
    const quill = quillRef.current?.getEditor?.();
    if (!quill) return;

    const qText = quill.getText() || '';
    const lower = qText.toLowerCase();

    let start = null;
    let len = 0;

    // Prefer originalText (more robust)
    const ot = typeof activeRange.originalText === 'string' ? activeRange.originalText.trim() : '';
    if (ot) {
      const idx = lower.indexOf(ot.toLowerCase());
      if (idx !== -1) {
        start = idx;
        len = Math.max(1, ot.length);
      }
    }

    // Fallback to numeric offsets
    if (start === null && Number.isFinite(+activeRange.start) && Number.isFinite(+activeRange.end)) {
      const s = clamp(0, qText.length, +activeRange.start);
      const e = clamp(0, qText.length, +activeRange.end);
      if (e > s) { start = s; len = e - s; }
    }

    if (start === null) return;

    const bounds = quill.getBounds(start, len);
    const root = quill.root;
    if (bounds && root) {
      root.scrollTop = Math.max(0, bounds.top + root.scrollTop - 60);
      quill.setSelection(start, len, 'user');

      // temporary blue blink to emphasize jump
      isApplyingRef.current = true;
      try {
        quill.formatText(start, len, 'background', 'rgba(37, 99, 235, 0.32)');
        setTimeout(() => applyInlineMarkups(), 450);
      } finally {
        isApplyingRef.current = false;
      }
    }
  }, [activeRange, applyInlineMarkups]);


  // Tooltip / quick correction (disabled in readOnly)
  useEffect(() => {
    if (readOnly) return;
    const quill = quillRef.current?.getEditor?.();
    if (!quill || !showMarkups) return;

    const onClick = () => {
      const sel = quill.getSelection();
      if (!sel) return;

      const qText = quill.getText() || '';
      const match = findOverlappingMark(normalizeMarks, sel, qText);
      if (match) showErrorDetails(match);
    };

    const el = quill.root;
    el.addEventListener('click', onClick);
    return () => el.removeEventListener('click', onClick);
  }, [normalizeMarks, showMarkups, readOnly]);

  const showErrorDetails = (mark) => {
    const old = document.querySelector('.markup-tooltip');
    if (old) old.remove();

    const tooltip = document.createElement('div');
    tooltip.className = 'markup-tooltip';
    tooltip.innerHTML = `
      <div class="tooltip-content">
        <div class="tooltip-header"><strong>${escapeHtml(mark.message || 'Marcação')}</strong></div>
        <div class="tooltip-body">
          ${mark.originalText ? `<div class="tooltip-original">Original: "${escapeHtml(mark.originalText)}"</div>` : ''}
          ${mark.correction ? `<div class="tooltip-correction">Sugestão: "${escapeHtml(mark.correction)}"</div>` : ''}
        </div>
        <div class="tooltip-actions">
          ${!readOnly && mark.originalText && mark.correction ? `<button class="tooltip-btn apply-btn" data-action="apply">Aplicar</button>` : ''}
          <button class="tooltip-btn dismiss-btn" data-action="dismiss">Fechar</button>
        </div>
      </div>
    `;

    const quill = quillRef.current?.getEditor?.();
    const sel = quill?.getSelection();
    if (quill && sel) {
      const bounds = quill.getBounds(sel.index, Math.max(1, sel.length));
      const rect = quill.root.getBoundingClientRect();
      tooltip.style.position = 'absolute';
      tooltip.style.top = `${rect.top + bounds.bottom + window.scrollY + 6}px`;
      tooltip.style.left = `${rect.left + bounds.left + window.scrollX}px`;
      tooltip.style.zIndex = '1000';
      document.body.appendChild(tooltip);
    } else {
      document.body.appendChild(tooltip);
    }

    tooltip.addEventListener('click', (e) => {
      const action = e.target?.dataset?.action;
      if (action === 'apply' && !readOnly && mark.originalText && mark.correction) {
        applyCorrection(mark.originalText, mark.correction);
      }
      tooltip.remove();
    });

    setTimeout(() => { if (tooltip.parentNode) tooltip.remove(); }, 10000);
  };

  const applyCorrection = (original, correction) => {
    const quill = quillRef.current?.getEditor?.();
    if (!quill) return;

    const qText = quill.getText() || '';
    const idx = qText.toLowerCase().indexOf(original.toLowerCase());
    if (idx !== -1) {
      quill.deleteText(idx, original.length);
      quill.insertText(idx, correction);
      const html = quill.root.innerHTML;
      setEditorContent(html);
      onChange?.(html, stripHtml(html));
      setTimeout(() => applyInlineMarkups(), 10);
    }
  };

  return (
    <div className="inline-markup-editor">
      <ReactQuill
        ref={quillRef}
        value={editorContent}
        onChange={handleContentChange}
        theme="snow"
        modules={modules || defaultModules}
        formats={defaultFormats}
        placeholder={placeholder}
        readOnly={readOnly}
      />

      {Array.isArray(inlineErrors) && inlineErrors.length > 0 && (
        <div className="markup-summary">
          <div className="markup-count">
            {inlineErrors.length} issue{inlineErrors.length !== 1 ? 's' : ''} found
          </div>
          <div className="markup-legend">
            <LegendItem icon="⚠️" label="Grammar" />
            <LegendItem icon="📝" label="Spelling" />
            <LegendItem icon="❗" label="Punctuation" />
            <LegendItem icon="🔄" label="Agreement" />
            <LegendItem icon="🏗️" label="Structure" />
            <LegendItem icon="💭" label="Argument" />
            <LegendItem icon="🎨" label="Style" />
          </div>
        </div>
      )}
    </div>
  );
};

export default InlineMarkupEditor;

/* helpers */
function LegendItem({ icon, label }) {
  return (
    <span className="legend-item">
      <span className="legend-icon">{icon}</span> {label}
    </span>
  );
}
function escapeHtml(str) {
  return String(str || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
function clamp(lo, hi, n) {
  const x = Number.isFinite(+n) ? +n : 0;
  return Math.max(lo, Math.min(hi, x));
}

// Find a mark overlapping current selection
function findOverlappingMark(marks, sel, qText) {
  const lower = (qText || '').toLowerCase();
  for (const m of marks) {
    if (Number.isFinite(m.start) && Number.isFinite(m.end)) {
      if (sel.index >= m.start && sel.index < m.end) return m;
    } else if (m.originalText) {
      const idx = lower.indexOf(m.originalText.toLowerCase());
      if (idx !== -1) return { ...m, start: idx, end: idx + m.originalText.length };
    }
  }
  return null;
}
